import { Suspense, lazy } from "react";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router";
import RequireDmEnabled from "@/components/RequireDmEnabled";

// Auto-reload on stale chunk after deploy (hash mismatch)
function lazyRetry(factory: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : "";
      if (
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed") ||
        msg.includes("error loading dynamically imported module")
      ) {
        const key = "chunk_reload";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
        }
        // Return a no-op component to prevent the error boundary from firing
        return { default: () => null } as { default: React.ComponentType };
      }
      throw err;
    }),
  );
}

const AppShell = lazyRetry(() => import("@/components/layout/AppShell"));
const Login = lazyRetry(() => import("@/pages/Login"));
const Dashboard = lazyRetry(() => import("@/pages/Dashboard"));
const SendFax = lazyRetry(() => import("@/pages/SendFax"));
const BulkSend = lazyRetry(() => import("@/pages/BulkSend"));
const SendHistory = lazyRetry(() => import("@/pages/SendHistory"));
const ReceiveHistory = lazyRetry(() => import("@/pages/ReceiveHistory"));
const OcrResults = lazyRetry(() => import("@/pages/OcrResults"));
const OcrReview = lazyRetry(() => import("@/pages/OcrReview"));
const OcrAudit = lazyRetry(() => import("@/pages/OcrAudit"));
const Dictionary = lazyRetry(() => import("@/pages/Dictionary"));
const DictionarySuggestions = lazyRetry(() => import("@/pages/DictionarySuggestions"));
const ImportCodeMaps = lazyRetry(() => import("@/pages/ImportCodeMaps"));
const ProductMapping = lazyRetry(() => import("@/pages/ProductMapping"));
const Users = lazyRetry(() => import("@/pages/Users"));
const Settings = lazyRetry(() => import("@/pages/Settings"));
const Help = lazyRetry(() => import("@/pages/Help"));
const PlatformTenants = lazyRetry(() => import("@/pages/PlatformTenants"));
const PlatformTenantCreate = lazyRetry(() => import("@/pages/PlatformTenantCreate"));
const PlatformTenantDetail = lazyRetry(() => import("@/pages/PlatformTenantDetail"));
const PlatformDemoTenants = lazyRetry(() => import("@/pages/PlatformDemoTenants"));
const PlatformTrialApplications = lazyRetry(() => import("@/pages/PlatformTrialApplications"));
const Billing = lazyRetry(() => import("@/pages/Billing"));
const PlatformBilling = lazyRetry(() => import("@/pages/PlatformBilling"));
const PlatformIntakeRecords = lazyRetry(() => import("@/pages/PlatformIntakeRecords"));
const PlatformTrialFaxNumbers = lazyRetry(() => import("@/pages/PlatformTrialFaxNumbers"));
const PlatformTrialNotifications = lazyRetry(() => import("@/pages/PlatformTrialNotifications"));
const PlatformTrialDataRetentions = lazyRetry(() => import("@/pages/PlatformTrialDataRetentions"));
const PlatformTrialConversions = lazyRetry(() => import("@/pages/PlatformTrialConversions"));
const AiAttribution = lazyRetry(() => import("@/pages/AiAttribution"));
const PlatformLeads = lazyRetry(() => import("@/pages/PlatformLeads"));
const PlatformCustomerContext = lazyRetry(() => import("@/pages/PlatformCustomerContext"));
const Campaign = lazyRetry(() => import("@/pages/Campaign"));
const DmCampaigns = lazyRetry(() => import("@/pages/DmCampaigns"));
const DmCampaignDetail = lazyRetry(() => import("@/pages/DmCampaignDetail"));
const DmEngagement = lazyRetry(() => import("@/pages/DmEngagement"));
const DmSuppressions = lazyRetry(() => import("@/pages/DmSuppressions"));
const Connectors = lazyRetry(() => import("@/pages/Connectors"));
const SphereChatManagement = lazyRetry(() => import("@/pages/SphereChatManagement"));
const SpfaxChatManagement = lazyRetry(() => import("@/pages/SpfaxChatManagement"));
const ChatQA = lazyRetry(() => import("@/pages/ChatQA"));
const NotifySubscribers = lazyRetry(() => import("@/pages/NotifySubscribers"));
const ColumnManager = lazyRetry(() => import("@/pages/ColumnManager"));
const Changelog = lazyRetry(() => import("@/pages/Changelog"));
const Announcements = lazyRetry(() => import("@/pages/Announcements"));
const OrderConfirmationTemplates = lazyRetry(() => import("@/pages/OrderConfirmationTemplates"));
const Advertisers = lazyRetry(() => import("@/pages/Advertisers"));
const AdvertiserPortal = lazyRetry(() => import("@/pages/AdvertiserPortal"));

// Clear the reload guard so future deploys can trigger another reload
sessionStorage.removeItem("chunk_reload");

function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 shadow-sm">
        画面を読み込み中...
      </div>
    </div>
  );
}

function withSuspense(node: React.ReactNode) {
  return <Suspense fallback={<PageFallback />}>{node}</Suspense>;
}

const router = createBrowserRouter([
  { path: "/login", element: withSuspense(<Login />) },
  { path: "/advertiser", element: withSuspense(<AdvertiserPortal />) },
  {
    path: "/",
    element: withSuspense(<AppShell />),
    children: [
      { index: true, element: withSuspense(<Dashboard />) },
      { path: "send", element: withSuspense(<SendFax />) },
      { path: "bulk", element: withSuspense(<BulkSend />) },
      { path: "sent", element: withSuspense(<SendHistory />) },
      { path: "received", element: withSuspense(<ReceiveHistory />) },
      { path: "ocr", element: withSuspense(<OcrResults />) },
      { path: "ocr/audit", element: withSuspense(<OcrAudit />) },
      { path: "ocr/:id/review", element: withSuspense(<OcrReview />) },
      { path: "dictionary", element: withSuspense(<Dictionary />) },
      { path: "dictionary/suggestions", element: withSuspense(<DictionarySuggestions />) },
      { path: "import-code-maps", element: withSuspense(<ImportCodeMaps />) },
      { path: "product-mapping", element: withSuspense(<ProductMapping />) },
      { path: "billing", element: withSuspense(<Billing />) },
      { path: "users", element: withSuspense(<Users />) },
      { path: "advertisers", element: withSuspense(<Advertisers />) },
      { path: "settings", element: withSuspense(<Settings />) },
      { path: "order-confirmation-templates", element: withSuspense(<OrderConfirmationTemplates />) },
      { path: "help", element: withSuspense(<Help />) },
      { path: "platform/tenants", element: withSuspense(<PlatformTenants />) },
      { path: "platform/tenants/new", element: withSuspense(<PlatformTenantCreate />) },
      { path: "platform/tenants/:id", element: withSuspense(<PlatformTenantDetail />) },
      { path: "platform/demo-tenants", element: withSuspense(<PlatformDemoTenants />) },
      { path: "platform/trial-applications", element: withSuspense(<PlatformTrialApplications />) },
      { path: "platform/billing", element: withSuspense(<PlatformBilling />) },
      { path: "platform/intake-records", element: withSuspense(<PlatformIntakeRecords />) },
      { path: "platform/trial-fax-numbers", element: withSuspense(<PlatformTrialFaxNumbers />) },
      { path: "platform/trial-notifications", element: withSuspense(<PlatformTrialNotifications />) },
      { path: "platform/trial-data-retentions", element: withSuspense(<PlatformTrialDataRetentions />) },
      { path: "platform/trial-conversions", element: withSuspense(<PlatformTrialConversions />) },
      { path: "platform/ai-attribution", element: withSuspense(<AiAttribution />) },
      { path: "platform/leads", element: withSuspense(<PlatformLeads />) },
      { path: "platform/customer-context", element: withSuspense(<PlatformCustomerContext />) },
      { path: "columns", element: withSuspense(<ColumnManager />) },
      { path: "connectors", element: withSuspense(<Connectors />) },
      { path: "campaign", element: withSuspense(<Campaign />) },
      { path: "dm-campaigns", element: withSuspense(<RequireDmEnabled><DmCampaigns /></RequireDmEnabled>) },
      { path: "dm-campaigns/:id", element: withSuspense(<RequireDmEnabled><DmCampaignDetail /></RequireDmEnabled>) },
      { path: "dm-engagement", element: withSuspense(<RequireDmEnabled viewerAccess allowReplyIntakeTenant><DmEngagement /></RequireDmEnabled>) },
      { path: "dm-suppressions", element: withSuspense(<RequireDmEnabled allowReplyIntakeTenant><DmSuppressions /></RequireDmEnabled>) },
      { path: "chat", element: <Navigate to="/chat/sphere" replace /> },
      { path: "chat/sphere", element: withSuspense(<SphereChatManagement />) },
      { path: "chat/spfax", element: withSuspense(<SpfaxChatManagement />) },
      { path: "chat/qa", element: withSuspense(<ChatQA />) },
      { path: "platform/notify", element: withSuspense(<NotifySubscribers />) },
      { path: "platform/changelog", element: withSuspense(<Changelog />) },
      { path: "platform/announcements", element: withSuspense(<Announcements />) },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
